import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import service from '../service';
import type { Space } from '../types/venue';
import type { UpdateSpacePayload } from '../types/api';

export interface UpdateSpaceVariables {
  spaceId: string;
  payload: UpdateSpacePayload;
}

export type UpdateSpaceResult = Space;

/**
 * useUpdateSpace
 *
 * Mutation hook for updating a space.
 *
 * @example
 * const { mutateAsync: updateSpace, isPending, error } = useUpdateSpace();
 *
 * await updateSpace({
 *   spaceId: '123',
 *   payload: {
 *     assets: ['asset-id-1', 'asset-id-2'],
 *     featuredAssetId: 'asset-id-1',
 *   },
 * });
 */
export function useUpdateSpace(
  options?: UseMutationOptions<UpdateSpaceResult, Error, UpdateSpaceVariables>,
): UseMutationResult<UpdateSpaceResult, Error, UpdateSpaceVariables> {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<UpdateSpaceResult, Error, UpdateSpaceVariables>({
    mutationKey: ['space', 'update'],
    mutationFn: async ({ spaceId, payload }) => {
      return service.updateSpace(spaceId, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate space queries to refresh data including images
      queryClient.invalidateQueries({ queryKey: ['space', variables.spaceId] });
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
      // Also invalidate any space queries with different include params
      queryClient.invalidateQueries({
        queryKey: ['space'],
        exact: false,
      });
      onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
